import {
  ServicesRendered,
  TimesheetLine,
  type TimesheetLineList,
  TimesheetReq,
} from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';

import { TimesheetLineClientService, TransactionClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type TimeSheetFilters = { date?: Date };
export const getTimeSheetQueryKeys = (filter: TimeSheetFilters) =>
  queryKeys.timeSheet.getTimeSheetHash(filter);

export const useTimeSheetCheckQuery = ({
  userId,
  enabled = true,
}: {
  userId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.timeSheet.root, queryKeys.timeSheet.timeSheetCheck, userId],
    queryFn: () => {
      return TransactionClientService.timesheetCheck(userId);
    },
    enabled,
  });
};

export const timeSheetQueryConfig = {
  getQueryKeys: (
    timesheetOwnerId: number,
    dateStart: string,
    dateEnd: string,
    filters: TimeSheetFilters = {},
  ) => [
    queryKeys.timeSheet.root,
    timesheetOwnerId,
    dateStart,
    dateEnd,
    queryKeys.timeSheet.getTimeSheetHash(filters),
  ],
  getQueryFn: (timesheetOwnerId: number, dateStart: string, dateEnd: string) => () => {
    const sr = ServicesRendered.create();
    sr.isActive = 1;
    sr.hideFromTimesheet = 0;
    sr.technicianUserId = timesheetOwnerId;
    const tl = TimesheetLine.create();
    tl.isActive = 1;
    tl.technicianUserId = timesheetOwnerId;

    const req = TimesheetReq.create();
    req.servicesRendered = sr;
    req.timesheetLine = tl;
    return TimesheetLineClientService.GetTimesheet(
      req,
      `${dateStart}%`,
      `${format(addDays(parseISO(dateEnd), 1), 'yyyy-MM-dd')}%`,
    );
  },
};

export const useTimeSheetQuery = ({
  args: { timesheetOwnerId, shownDates },
  filters = {},
}: {
  args: {
    timesheetOwnerId: number;
    shownDates: string[];
  };
  filters?: TimeSheetFilters;
}) => {
  return useQuery({
    queryKey: timeSheetQueryConfig.getQueryKeys(
      timesheetOwnerId,
      shownDates.at(0) || '',
      shownDates.at(-1) || '',
      filters,
    ),
    queryFn: timeSheetQueryConfig.getQueryFn(
      timesheetOwnerId,
      shownDates.at(0) || '',
      shownDates.at(-1) || '',
    ),
  });
};

export type TimesheetLineFilter = EntityFilter<TimesheetLine>;
export const useTimesheetManagerQuery = <TSelectData = TimesheetLineList,>({
  enabled,
  filter = {},
  select,
}: {
  select?: (data?: TimesheetLineList) => TSelectData;
  filter?: TimesheetLineFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.timeSheet.root,
      queryKeys.timeSheet.list,
      queryKeys.timeSheet.manager,
      queryKeys.timeSheet.getHash(filter),
    ],
    queryFn: async () => {
      const req = TimesheetLine.create(filter);
      return await TimesheetLineClientService.BatchGetManager(req);
    },
    enabled,
    select,
  });
};

export const useTimesheetPayrollQuery = <TSelectData = TimesheetLineList,>({
  enabled,
  filter = {},
  select,
}: {
  select?: (data?: TimesheetLineList) => TSelectData;
  filter?: TimesheetLineFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.timeSheet.root,
      queryKeys.timeSheet.list,
      queryKeys.timeSheet.payroll,
      queryKeys.timeSheet.getHash(filter),
    ],
    queryFn: () => TimesheetLineClientService.BatchGetPayroll(TimesheetLine.create(filter)),
    select,
    enabled,
  });
};

export const useTimesheetLineBatchGetQuery = ({
  filter = {},
  enabled,
}: {
  filter?: TimesheetLineFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.timeSheet.root,
      queryKeys.timeSheet.list,
      queryKeys.timeSheet.getHash(filter),
    ],
    queryFn: () => TimesheetLineClientService.BatchGet(TimesheetLine.create(filter)),
    enabled,
  });
};
